import { useEffect, useMemo, useState } from 'react'
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, useMap } from 'react-leaflet'
import { api } from '@pilot-hub/api-client'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Flugroute eines abgeschlossenen Fluges: gespeicherter Flugweg (flug_telemetrie, von dji-sync, alle 3 s)
// mit Start/Landung und Kennzahlen. Ohne Flugweg (manuell erfasste Flüge) nur Start-/Landeort.

interface Kennzahlen {
  punkte: number; von: string; bis: string; strecke_m: number
  max_hoehe_m: number | null; max_entfernung_m: number; max_tempo_kmh: number | null
}
interface Flugweg { punkte: [number, number, number | null, string, number | null][]; kennzahlen: Kennzahlen | Record<string, never> }

const GRUNDKARTEN = {
  karte: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap', maxNativeZoom: 19 },
  satellit: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Luftbild: &copy; Esri, Maxar', maxNativeZoom: 19 },
}

const zahl = (v: number | null | undefined, stellen = 0) =>
  v === null || v === undefined ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen })
const strecke = (m: number | null | undefined) => (m === null || m === undefined ? '–' : m >= 1000 ? `${zahl(m / 1000, 2)} km` : `${zahl(m)} m`)
const uhrzeit = (s: string) => new Date(s).toLocaleTimeString('de-DE')

const marke = (text: string, farbe: string) => L.divIcon({
  className: '',
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${farbe};border:3px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);color:#fff;font:700 12px/20px Arial,sans-serif;text-align:center">${text}</div>`,
})

function Ausschnitt({ punkte }: { punkte: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    // Erst nach dem Layout ausrichten: vorher kennt Leaflet die endgültige Kartengröße nicht (zu nah gezoomt)
    const ausrichten = () => {
      map.invalidateSize()
      if (punkte.length === 1) map.setView(punkte[0], 17)
      else if (punkte.length > 1) map.fitBounds(L.latLngBounds(punkte), { padding: [28, 28], maxZoom: 18 })
    }
    ausrichten()
    const t = setTimeout(ausrichten, 250)
    return () => clearTimeout(t)
  }, [map, punkte])
  return null
}

function koordinate(lat: unknown, lon: unknown): [number, number] | null {
  const a = Number(lat), b = Number(lon)
  return lat != null && lon != null && lat !== '' && lon !== '' && Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null
}

export function FlugroutenKarte({ flug }: { flug: { id: number; endzeit?: string | null; startort_lat?: unknown; startort_lon?: unknown; zielort_lat?: unknown; zielort_lon?: unknown } }) {
  const [daten, setDaten] = useState<Flugweg | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [grundkarte, setGrundkarte] = useState<keyof typeof GRUNDKARTEN>('karte')

  useEffect(() => {
    setDaten(null); setFehler(null)
    api.get(`/fluege/${flug.id}/flugweg`)
      .then(r => setDaten(r.data as Flugweg))
      .catch(e => setFehler(e?.response?.data?.detail || 'Flugroute konnte nicht geladen werden'))
  }, [flug.id])

  const weg = useMemo(() => (daten?.punkte ?? []).map(p => [p[0], p[1]] as [number, number]), [daten])
  const start = weg[0] ?? koordinate(flug.startort_lat, flug.startort_lon)
  const landung = weg.length ? (flug.endzeit ? weg[weg.length - 1] : null) : koordinate(flug.zielort_lat, flug.zielort_lon)
  const ausschnitt = useMemo(() => (weg.length ? weg : [start, landung].filter((p): p is [number, number] => !!p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weg, start?.[0], start?.[1], landung?.[0], landung?.[1]])
  const k = daten?.kennzahlen && 'punkte' in daten.kennzahlen ? daten.kennzahlen as Kennzahlen : null
  const gk = GRUNDKARTEN[grundkarte]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-dark-text">Flugroute</div>
          <div className="text-xs text-dark-text-muted">
            {!daten && !fehler ? 'Lade Flugweg…' : weg.length ? `Aufgezeichnet ${uhrzeit(k!.von)} – ${uhrzeit(k!.bis)} Uhr`
              : 'Kein Flugweg aufgezeichnet (nur bei Flügen über DJI Pilot 2)'}
          </div>
        </div>
        {ausschnitt.length > 0 && (
          <div className="flex rounded-lg border border-dark-border p-0.5" role="group" aria-label="Hintergrundkarte">
            {(['karte', 'satellit'] as const).map(g => (
              <button key={g} type="button" aria-pressed={grundkarte === g} onClick={() => setGrundkarte(g)}
                className={`min-h-[36px] rounded-md px-3 text-xs font-medium ${grundkarte === g ? 'bg-primary-600 text-white' : 'text-dark-text-muted hover:text-dark-text'}`}>
                {g === 'karte' ? 'Karte' : 'Satellit'}
              </button>
            ))}
          </div>
        )}
      </div>

      {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{fehler}</div>}

      {ausschnitt.length > 0 && (
        <div className="relative z-0 h-[340px] overflow-hidden rounded-lg border border-dark-border sm:h-[420px]">
          <MapContainer center={ausschnitt[0]} zoom={16} className="h-full w-full" scrollWheelZoom>
            <TileLayer key={grundkarte} url={gk.url} attribution={gk.attribution} maxNativeZoom={gk.maxNativeZoom} maxZoom={20} />
            {weg.length > 1 && <>
              <Polyline positions={weg} pathOptions={{ color: '#ffffff', weight: 6, opacity: 0.9 }} />
              <Polyline positions={weg} pathOptions={{ color: '#d9282f', weight: 3 }} />
            </>}
            {landung && <Marker position={landung} icon={marke('L', '#15191f')}><Tooltip direction="top" offset={[0, -12]}>Landung</Tooltip></Marker>}
            {start && <Marker position={start} icon={marke('S', '#1f8c4c')}><Tooltip direction="top" offset={[0, -12]}>Start</Tooltip></Marker>}
            <Ausschnitt punkte={ausschnitt} />
          </MapContainer>
        </div>
      )}

      {k && (
        <dl className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          {[
            ['Geflogene Strecke', strecke(k.strecke_m)],
            ['Max. Höhe über Start', `${zahl(k.max_hoehe_m)} m`],
            ['Max. Entfernung vom Start', strecke(k.max_entfernung_m)],
            ['Max. Geschwindigkeit', `${zahl(k.max_tempo_kmh)} km/h`],
          ].map(([l, w]) => (
            <div key={l} className="rounded-lg border border-dark-border bg-dark-bg-tertiary/40 px-2 py-2">
              <dt className="text-[11px] text-dark-text-subtle">{l}</dt>
              <dd className="text-sm font-semibold text-dark-text tabular-nums">{w}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
